import React, { use, useCallback, useMemo } from "react";
import { ScrollView } from "react-native";
import {
    InvitationTarget,
    KycStatus,
    Mission,
    WorkerInfo,
    WorkingPermitStatus,
} from "../../__generated__/graphql";
import { useI18n } from "../../hooks/i18n-hooks";
import { Buildable } from "ts-essentials";
import { useQuery } from "@apollo/client/react";
import LoadingBoundary from "../../ui_atoms/LoadingBoundary";
import ListItem from "../../ui_atoms/ListItem";
import { GET_CLIENT_WORKERS_AND_INVITATION_WORKERS } from "../../graphql/client";
import { LabelMode } from "../../ui_atoms/Label";
import { useRelevantMissionCounter } from "../../hooks/missions";
import { useTheme } from "../../hooks/use-theme";
import Gap from "../../ui_atoms/Gap";
import { LinkMediumText } from "../FormattedText";
import { useNavigation } from "@react-navigation/native";
import { RootStackParamList } from "../../navigations/RootNavigation";
import { StackScreenProps } from "@react-navigation/stack";
import IconComposition from "../IconComposition";
import {
    CreateMissionMode,
    CreateMissionScreensContext,
} from "../../contexts/CreateMissionManager";

type WorkerInfoCellProps = {
    workerInfo: WorkerInfo;
    setWorkerInfo: (workerInfo: WorkerInfo) => void;
    selectedId?: string;
};
type ScreenParams = StackScreenProps<RootStackParamList>;

const WorkerInfoCell: React.FC<WorkerInfoCellProps> = ({
    workerInfo,
    setWorkerInfo,
    selectedId,
}) => {
    const i18n = useI18n();
    const { colors, gridSize } = useTheme();
    const isInvitation = "isInInvitation" in workerInfo;
    const isTestWorker = "isTestWorker" in workerInfo;
    const isWorkerValid = workerInfo.kycStatus === KycStatus.Accepted || isTestWorker;
    const getMissionCount = useRelevantMissionCounter();
    const [label, labelMode] = useMemo(() => {
        if (isInvitation) {
            return [
                i18n.t("screens.user.create_mission_worker.is_invitation"),
                "warning" as LabelMode,
            ];
        }
        if (workerInfo.workPermitStatus === WorkingPermitStatus.None) {
            return [i18n.t("screens.user.create_mission_worker.no_permit"), "default" as LabelMode];
        }
        if (workerInfo.workPermitStatus === WorkingPermitStatus.InProcess) {
            return [
                i18n.t("screens.user.create_mission_worker.permit_in_process"),
                "default" as LabelMode,
            ];
        }
        if (!isWorkerValid) {
            return [
                i18n.t("screens.user.create_mission_worker.incomplete_profile"),
                "default" as LabelMode,
            ];
        }
        return [undefined, undefined];
    }, [i18n, isInvitation, workerInfo.workPermitStatus, isWorkerValid]);

    const onPress = useCallback(() => {
        setWorkerInfo(workerInfo);
    }, [setWorkerInfo, workerInfo]);

    const testWorkerIcon = useMemo(() => {
        return (
            <IconComposition
                backgroundColor={colors.secondary}
                iconColor={colors.onPrimary}
                icon="lightbulb"
                size={21}
            />
        );
    }, [colors]);
    return (
        <ListItem
            selectable
            key={workerInfo.id}
            title={workerInfo.name || workerInfo.phoneNumber || ""}
            useBigAvatarOrIcon
            style={{
                marginHorizontal: -gridSize,
                paddingHorizontal: gridSize,
                paddingBottom: gridSize,
            }}
            icon={isInvitation ? "talent" : undefined}
            iconComponent={isTestWorker ? testWorkerIcon : undefined}
            avatarPath={isInvitation || isTestWorker ? undefined : workerInfo.avatarUrl}
            description={
                isTestWorker
                    ? i18n.t("screens.user.create_mission_worker.test_worker_description")
                    : isInvitation
                      ? undefined
                      : i18n.t("screens.user.create_mission_worker.worker_card_description", {
                            count: getMissionCount(workerInfo.missionCount),
                            nbMissions: getMissionCount(workerInfo.missionCount),
                        })
            }
            labels={[{ text: label, mode: labelMode }]}
            disabled={!isWorkerValid}
            checked={workerInfo.id === selectedId}
            onPress={onPress}
        />
    );
};

type MissionWorkerProps = {
    mission: Buildable<Mission>;
    setWorkerInfo: (workerInfo: WorkerInfo) => void;
};

const MissionWorker: React.FC<MissionWorkerProps> = ({ mission, setWorkerInfo }) => {
    const i18n = useI18n();
    const { createMissionMode } = use(CreateMissionScreensContext);
    const { loading, error, data } = useQuery(GET_CLIENT_WORKERS_AND_INVITATION_WORKERS, {
        fetchPolicy: "network-only",
    });
    const navigator = useNavigation<ScreenParams["navigation"]>();
    const itemsObject = useMemo(() => {
        const result = [];

        // Ajouter le travailleur de test spécial en premier
        const testWorker = {
            id: "test-worker-special",
            name: i18n.t("screens.user.create_mission_worker.test_worker"),
            address: {
                formatted: "Test Address, 1000 Test City",
                route: "Test Street",
                streetNumber: "1",
                longitude: 0,
                latitude: 0,
                locality: {
                    name: "Test City",
                    postalCode: "1000",
                    state: {
                        name: "Test State",
                        code: "TS",
                        country: {
                            name: "Switzerland",
                            code: "CH",
                        },
                    },
                },
            },
            avatarUrl: null,
            avsNumber: null,
            birthdate: "1990-01-01",
            curriculumVitae: null,
            deleted: false,
            documents: null,
            educationLevel: null,
            email: "test@example.com",
            experiences: null,
            gender: "male",
            hasExperience: false,
            missionCount: {
                total: 0,
                draft: 0,
                pendingAcceptation: 0,
                accepted: 0,
                ended: 0,
                terminated: 0,
                cancelled: 0,
            },
            missions: [],
            phoneNumber: "+41000000000",
            workPermitStatus: "PERMIT_C" as WorkingPermitStatus,
            yearsOfExperience: 0,
            isTestWorker: true,
            deletedAt: false,
        };

        if (data?.getClientWorkers) {
            result.push(...data.getClientWorkers);
        }
        if (createMissionMode === CreateMissionMode.New) {
            result.push(testWorker);
        }
        if (data?.workerInvitationsSent) {
            result.push(
                ...data.workerInvitationsSent.map((invitation) => {
                    let name = "";
                    if (invitation.firstName) {
                        name = `${invitation.firstName}`;
                    }
                    if (invitation.lastName) {
                        name = `${name} ${invitation.lastName}`;
                    }
                    if (!name) {
                        // An invitation sent by email (from the web app) has no phone number.
                        name = invitation.phoneNumber || invitation.email || "";
                    }
                    return {
                        id: invitation.id,
                        name,
                        address: null,
                        birthdate: null,
                        missionCount: null,
                        missions: [],
                        isInInvitation: true,
                        deletedAt: false,
                    };
                }),
            );
        }
        return result;
    }, [createMissionMode, data?.getClientWorkers, data?.workerInvitationsSent, i18n]);

    return (
        <LoadingBoundary loading={loading} error={error}>
            <ScrollView
                style={{
                    overflow: "visible",
                }}
            >
                {itemsObject.map((workerInfo) => {
                    return (
                        <WorkerInfoCell
                            key={workerInfo.id}
                            workerInfo={workerInfo as WorkerInfo}
                            setWorkerInfo={setWorkerInfo}
                            selectedId={mission.workerInfo?.id}
                        />
                    );
                })}
                {!loading && createMissionMode === CreateMissionMode.New && (
                    <>
                        <Gap gap={1} />
                        <LinkMediumText
                            style={{ alignSelf: "center" }}
                            onPress={() => {
                                // Open invitation screen
                                navigator.navigate("SendInvitation", {
                                    target: InvitationTarget.Worker,
                                });
                            }}
                        >
                            {i18n.t("screens.user.create_mission_worker.invite_worker")}
                        </LinkMediumText>
                        <Gap gap={1} />
                    </>
                )}
            </ScrollView>
        </LoadingBoundary>
    );
};

export default MissionWorker;
